import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X, Eye } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = { slug: "", title: "", content: "", meta_description: "", published: true };

export default function AdminPages() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/pages").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id) {
        await api.put(`/admin/pages/${data.id}`, data);
        toast.success("Página guardada");
      } else {
        await api.post("/admin/pages", data);
        toast.success("Página creada");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta página?")) return;
    try {
      await api.delete(`/admin/pages/${id}`);
      toast.success("Página eliminada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-pages">
      <div className="flex justify-between items-start mb-8">
        <div>
          <p className="overline mb-3">CMS</p>
          <h1 className="font-heading text-4xl text-ivory">Páginas estáticas</h1>
          <p className="text-sm text-ivory/60 mt-3 max-w-xl">Crea y gestiona páginas legales, informativas o de marca. Cada página se publica automáticamente en /p/[slug].</p>
        </div>
        <button onClick={() => setEditing(EMPTY)} className="btn-gold !py-2 !px-4 flex items-center gap-2" data-testid="new-page-btn">
          <Plus size={14} /> Nueva página
        </button>
      </div>

      <div className="bg-graphite border border-smoke overflow-x-auto">
        <table className="admin-table w-full">
          <thead>
            <tr><th>Título</th><th>Slug · URL pública</th><th>Estado</th><th>Actualizada</th><th></th></tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} data-testid={`page-${p.slug}`}>
                <td className="text-ivory">{p.title}</td>
                <td className="text-ivory/70 font-mono text-xs">/p/{p.slug}</td>
                <td>
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${p.published ? "border-gold text-gold" : "border-smoke text-ivory/40"}`}>
                    {p.published ? "Publicada" : "Borrador"}
                  </span>
                </td>
                <td className="text-ivory/40 text-xs">{p.updated_at ? new Date(p.updated_at).toLocaleDateString("es-ES") : "-"}</td>
                <td>
                  <div className="flex gap-2">
                    <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="text-ivory/60 hover:text-gold" title="Ver"><Eye size={14} /></a>
                    <button onClick={() => setEditing(p)} className="text-ivory/60 hover:text-gold" data-testid={`edit-page-${p.slug}`}><Edit size={14} /></button>
                    <button onClick={() => del(p.id)} className="text-ivory/60 hover:text-rose" data-testid={`del-page-${p.slug}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="5" className="text-center text-ivory/40 py-12">No hay páginas</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-carbon/90 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-graphite border border-smoke max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b border-smoke">
              <h2 className="font-heading text-2xl text-ivory">{editing.id ? "Editar página" : "Nueva página"}</h2>
              <button onClick={() => setEditing(null)} className="text-ivory"><X size={20} /></button>
            </div>
            <form onSubmit={e => { e.preventDefault(); save(editing); }} className="p-6 space-y-4">
              <div><label className="luxe-label">Título</label><input data-testid="page-form-title" required value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className="luxe-input" /></div>
              <div><label className="luxe-label">Slug (sin espacios, ej: nuestra-historia)</label><input data-testid="page-form-slug" required value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value })} className="luxe-input" /></div>
              <div><label className="luxe-label">Meta descripción (SEO)</label><input value={editing.meta_description || ""} onChange={e => setEditing({ ...editing, meta_description: e.target.value })} className="luxe-input" /></div>
              <div>
                <label className="luxe-label">Contenido (texto + saltos de línea)</label>
                <textarea data-testid="page-form-content" required value={editing.content} onChange={e => setEditing({ ...editing, content: e.target.value })} className="luxe-input border border-smoke px-3 py-3 min-h-[260px]" />
              </div>
              <label className="flex items-center gap-2 text-ivory">
                <input type="checkbox" checked={editing.published} onChange={e => setEditing({ ...editing, published: e.target.checked })} className="accent-gold" /> Publicada
              </label>
              <div className="flex gap-3 pt-4 border-t border-smoke">
                <button type="submit" className="btn-gold" data-testid="page-form-save">{editing.id ? "Guardar cambios" : "Crear página"}</button>
                <button type="button" onClick={() => setEditing(null)} className="btn-outline-gold">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
